import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchControllers } from '@/features/controllers/api'
import {
  checkReloadStatus,
  reloadControllerData,
  updatePortfolioList,
  type ReloadPeriod,
  type ReloadTopic,
} from '@/features/iotManagement/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'

type Tab = 'energy-data' | 'midnight-data' | 'add-remove'

/**
 * Ports includes/iot_management.html's three live tabs (the fourth,
 * Performance Analysis, was already commented out/dead in the legacy
 * template - not ported). Reload buttons and the add/remove-portfolio form
 * both already had plain-JSON Flask endpoints (graphs.js's
 * request_iot_data()/forms.js's portfolio-list form handler called them
 * directly, no template rendering involved) - reused as-is.
 */
export default function IotManagementPage() {
  const [tab, setTab] = useState<Tab>('energy-data')
  const [search, setSearch] = useState('')
  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })

  if (controllersQuery.isLoading) return <LoadingState label="Loading controllers…" />
  if (controllersQuery.isError) {
    return (
      <ErrorState
        message={controllersQuery.error instanceof Error ? controllersQuery.error.message : 'Failed to load controllers.'}
        onRetry={() => controllersQuery.refetch()}
      />
    )
  }
  const controllers = controllersQuery.data ?? []
  const filteredControllers = controllers.filter((c) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (c.controller_name ?? '').toLowerCase().includes(q) || c.controller_id.toLowerCase().includes(q)
  })

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-ink">IoT Management</h1>

      <div className="flex gap-2 overflow-x-auto overscroll-x-contain border-b border-nav-line md:overflow-x-visible">
        <TabButton active={tab === 'energy-data'} onClick={() => setTab('energy-data')} label="Energy Data" />
        <TabButton active={tab === 'midnight-data'} onClick={() => setTab('midnight-data')} label="Midnight Data" />
        <TabButton active={tab === 'add-remove'} onClick={() => setTab('add-remove')} label="Add System To Portfolio List" />
      </div>

      {(tab === 'energy-data' || tab === 'midnight-data') && (
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
        />
      )}

      {tab === 'energy-data' && <EnergyDataTab controllers={filteredControllers} />}
      {tab === 'midnight-data' && <MidnightDataTab controllers={filteredControllers} />}
      {tab === 'add-remove' && <AddRemoveTab />}
    </div>
  )
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
        active ? 'border-brand text-brand' : 'border-transparent text-text-secondary hover:text-text-primary'
      }`}
    >
      {label}
    </button>
  )
}

function useReloadStatusPolling(onCompleted: (controllerId: string, message: string) => void) {
  const timersRef = useRef<Record<string, number>>({})

  function startPolling(controllerId: string, controllerName: string) {
    if (timersRef.current[controllerId]) window.clearInterval(timersRef.current[controllerId])
    const interval = window.setInterval(async () => {
      try {
        const { reloads } = await checkReloadStatus()
        const completed = reloads.find((r) => r.controller_id === controllerId)
        if (completed) {
          window.clearInterval(interval)
          delete timersRef.current[controllerId]
          const rows = completed.total_rows ? ` (${completed.total_rows.toLocaleString()} rows)` : ''
          onCompleted(controllerId, `${completed.controller_name || controllerName} background reload finished${rows}.`)
        }
      } catch {
        // silent, matches legacy behavior
      }
    }, 15000)
    timersRef.current[controllerId] = interval
    window.setTimeout(() => {
      if (timersRef.current[controllerId] === interval) {
        window.clearInterval(interval)
        delete timersRef.current[controllerId]
      }
    }, 30 * 60 * 1000)
  }

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      Object.values(timers).forEach((t) => window.clearInterval(t))
    }
  }, [])

  return startPolling
}

function useReloadAction() {
  const [pending, setPending] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ text: string; kind: 'success' | 'info' | 'error' } | null>(null)
  const startPolling = useReloadStatusPolling((_id, text) => setNotice({ text, kind: 'success' }))

  async function trigger(topic: ReloadTopic, period: ReloadPeriod, controllerId: string, controllerName: string) {
    const key = `${controllerId}:${period}`
    setPending(key)
    setNotice(null)
    try {
      const result = await reloadControllerData(topic, period, controllerId)
      if (result.background) {
        setNotice({
          text: `${controllerName}: background reload started, ~${result.estimated_time ?? 'a few minutes'}.`,
          kind: 'info',
        })
        startPolling(controllerId, controllerName)
      } else {
        setNotice({ text: result.message ?? 'Reload complete.', kind: 'success' })
      }
    } catch {
      setNotice({ text: 'Reload failed. Please try again.', kind: 'error' })
    } finally {
      setPending(null)
    }
  }

  return { trigger, pending, notice }
}

function EnergyDataTab({ controllers }: { controllers: { controller_id: string; controller_name: string | null }[] }) {
  const { trigger, pending, notice } = useReloadAction()
  if (controllers.length === 0) return <EmptyState message="No controllers match." />
  return (
    <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
      {notice && <Notice notice={notice} />}
      <table className="w-full text-[13.5px]">
        <thead>
          <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
            <th className="px-4 py-3.5 font-semibold">System</th>
            <th className="px-4 py-3.5 font-semibold">Reload Daily data</th>
            <th className="px-4 py-3.5 font-semibold">Reload Weekly data</th>
          </tr>
        </thead>
        <tbody>
          {controllers.map((c) => (
            <tr key={c.controller_id} className="border-b border-nav-line last:border-0">
              <td className="px-4 py-3.5">
                <Link to={`/analytics?controller=${c.controller_id}`} className="text-brand hover:underline">
                  {c.controller_name || c.controller_id}
                </Link>
              </td>
              <td className="px-4 py-3.5">
                <ReloadButton
                  disabled={pending === `${c.controller_id}:daily`}
                  onClick={() => trigger('energy_data_topic', 'daily', c.controller_id, c.controller_name || c.controller_id)}
                  label="Reload daily data"
                />
              </td>
              <td className="px-4 py-3.5">
                <ReloadButton
                  disabled={pending === `${c.controller_id}:weekly`}
                  onClick={() => trigger('energy_data_topic', 'weekly', c.controller_id, c.controller_name || c.controller_id)}
                  label="Reload weekly data"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MidnightDataTab({ controllers }: { controllers: { controller_id: string; controller_name: string | null }[] }) {
  const { trigger, pending, notice } = useReloadAction()
  if (controllers.length === 0) return <EmptyState message="No controllers match." />
  return (
    <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
      {notice && <Notice notice={notice} />}
      <table className="w-full text-[13.5px]">
        <thead>
          <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
            <th className="px-4 py-3.5 font-semibold">System</th>
            <th className="px-4 py-3.5 font-semibold">Reload</th>
          </tr>
        </thead>
        <tbody>
          {controllers.map((c) => (
            <tr key={c.controller_id} className="border-b border-nav-line last:border-0">
              <td className="px-4 py-3.5">
                <Link to={`/analytics?controller=${c.controller_id}`} className="text-brand hover:underline">
                  {c.controller_name || c.controller_id}
                </Link>
              </td>
              <td className="px-4 py-3.5">
                <ReloadButton
                  disabled={pending === `${c.controller_id}:midnight`}
                  onClick={() => trigger('midnight_data_topic', 'midnight', c.controller_id, c.controller_name || c.controller_id)}
                  label="Send Request"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ReloadButton({ disabled, onClick, label }: { disabled: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
    >
      {disabled ? 'Requesting…' : label}
    </button>
  )
}

function Notice({ notice }: { notice: { text: string; kind: 'success' | 'info' | 'error' } }) {
  const color = notice.kind === 'error' ? 'text-error' : notice.kind === 'info' ? 'text-accent' : 'text-brand'
  return <p className={`px-3 pt-3 text-sm ${color}`}>{notice.text}</p>
}

function AddRemoveTab() {
  const [actionType, setActionType] = useState<'1' | '2' | ''>('')
  const [controllerId, setControllerId] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!actionType) {
      setError('Please select an action to be performed!')
      return
    }
    if (!controllerId.trim()) {
      setError('Controller id field must not be empty!')
      return
    }
    setPending(true)
    try {
      const result = await updatePortfolioList(actionType, controllerId.trim())
      if (result.error) setError(result.error)
      else setSuccess(result.success ?? 'Done.')
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="max-w-sm rounded-2xl border border-nav-line bg-card p-5">
      <h2 className="mb-3 text-base font-semibold text-ink">Add Missing / Remove Duplicate Controller</h2>
      <form onSubmit={handleSubmit} className="space-y-3">
        <select
          required
          value={actionType}
          onChange={(e) => setActionType(e.target.value as '1' | '2' | '')}
          className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
        >
          <option value="">Select action</option>
          <option value="1">Add missing controller</option>
          <option value="2">Remove duplicate controller</option>
        </select>
        <input
          required
          placeholder="Controller id"
          value={controllerId}
          onChange={(e) => setControllerId(e.target.value)}
          className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
        />
        <button
          type="submit"
          disabled={pending}
          className="w-full rounded-full bg-brand py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {pending ? 'Adding…' : 'Add System'}
        </button>
        {error && <p className="text-sm text-error">{error}</p>}
        {success && <p className="text-sm text-brand">{success}</p>}
      </form>
    </div>
  )
}
